"use client";

type TauriInternals = {
  invoke?: <T = unknown>(command: string, args?: Record<string, unknown>) => Promise<T>;
};

type DesktopWindow = Window & {
  __TAURI_INTERNALS__?: TauriInternals;
};

function getDesktopInvoke() {
  if (typeof window === "undefined") {
    return undefined;
  }

  return (window as DesktopWindow).__TAURI_INTERNALS__?.invoke;
}

export function isDesktopNotificationRuntime() {
  return typeof getDesktopInvoke() === "function";
}

export function isSafeInAppHref(href: unknown): href is string {
  return typeof href === "string"
    && href.startsWith("/")
    && !href.startsWith("//")
    && !href.includes("\\");
}

export async function getDesktopMainWindowFocused(): Promise<boolean | undefined> {
  const invoke = getDesktopInvoke();
  if (!invoke) {
    return undefined;
  }

  try {
    return await invoke<boolean>("is_main_window_focused");
  } catch (error) {
    console.warn("[Findin Desktop] failed to get main window focus:", error);
    return undefined;
  }
}

export async function sendDesktopNotification(title: string, body?: string, href?: string) {
  const invoke = getDesktopInvoke();
  if (!invoke) {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("findin-desktop:notify", {
        detail: {
          title,
          body,
          href,
        },
      }));
    }
    return false;
  }

  try {
    await invoke("send_web_notification", {
      title,
      body,
      href: isSafeInAppHref(href) ? href : undefined,
    });
    return true;
  } catch (error) {
    console.warn("[Findin Desktop] failed to send notification:", error);
    return false;
  }
}

export async function openDesktopNotificationSettings() {
  const invoke = getDesktopInvoke();
  if (!invoke) {
    return false;
  }

  try {
    await invoke("open_notification_settings");
    return true;
  } catch (error) {
    console.warn("[Findin Desktop] failed to open notification settings:", error);
    return false;
  }
}
